import { Box, InputAdornment, TextField } from "@mui/material"
import { AddOutlined, FilterAltOutlined, LinkRounded, SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { Button } from "@/components/common/Button"
import { AerosSelect } from "@/components/UiKit/AerosSelect"
import type { Location } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { Option } from "@/types/form-fields"

interface CustomerProductsSearchBarProps {
  locations: Pick<Location, "locationId" | "name">[]
  searchQuery: string
  selectedLocationId: string | null
  onSearchChange: (query: string) => void
  onLocationChange: (locationId: string | null) => void
  onFilterOpen: () => void
  onAssociateProduct: () => void
  canEdit: boolean
}

const ALL_LOCATIONS_VALUE = ""

export const CustomerProductsSearchBar: FC<CustomerProductsSearchBarProps> = ({
  locations,
  searchQuery,
  selectedLocationId,
  onSearchChange,
  onLocationChange,
  onFilterOpen,
  onAssociateProduct,
  canEdit,
}) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const locationOptions: Option<string>[] = [
    { value: ALL_LOCATIONS_VALUE, label: "All Locations" },
    ...locations.map((loc) => ({ value: loc.locationId, label: loc.name })),
  ]

  const handleStartSo = () => {
    // TODO: Implement Start SO functionality
    notify({
      type: NotificationType.Warning,
      silent: false,
      message: "Not implemented",
    })
  }

  return (
    <Box className='flex items-center justify-between' data-testid='customer-products-search-bar'>
      <Box className='flex items-center gap-2'>
        <TextField
          data-testid='customer-products-search-input'
          value={searchQuery}
          variant='outlined'
          placeholder='Search Products'
          className='w-80'
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <Button
          aria-label='filter'
          appearance='outlined'
          onClick={onFilterOpen}
          startIcon={<FilterAltOutlined />}
          data-testid='customer-products-filter-button'
        >
          Filter
        </Button>

        <AerosSelect
          className='w-48'
          options={locationOptions}
          value={selectedLocationId ?? ALL_LOCATIONS_VALUE}
          onChange={(value) => onLocationChange(value || null)}
          data-testid='customer-products-location-select'
        />
      </Box>

      <Box className='flex items-center gap-2'>
        {canEdit && (
          <Button
            appearance='outlined'
            variant='primary'
            startIcon={<LinkRounded />}
            onClick={onAssociateProduct}
            data-testid='associate-product-button'
          >
            Associate Product
          </Button>
        )}

        <Button
          appearance='outlined'
          variant='primary'
          startIcon={<AddOutlined />}
          onClick={handleStartSo}
          data-testid='start-so-button'
        >
          Start SO
        </Button>
      </Box>
    </Box>
  )
}
